export type MileageBand = {
  serialNo: number;
  fromMiles: number;
  toMiles: number;
};

export type MileageRateCell = {
  serialNo: number;
  fromMiles: number;
  toMiles: number;
  ratePerMile: number | null;
};

export type MileageVehicle = {
  vehicleTypeId: string;
  description: string;
  seatCapacity: number | null;
  isMileageBase: boolean;
  minimumFare: number | null;
  upliftMode: "percent" | "flat" | null;
  upliftAmount: number | null;
  displayOrder: number | null;
  rates: MileageRateCell[];
};

const MILEAGE_VEHICLE_COLUMN_ORDER = [
  "Saloon Car",
  "Executive Car",
  "Estate Car",
  "People Carrier",
  "8 Seater Minibus",
];

export function sortMileageVehicles(vehicles: MileageVehicle[]): MileageVehicle[] {
  const orderIndex = (description: string) => {
    const idx = MILEAGE_VEHICLE_COLUMN_ORDER.indexOf(description);
    return idx === -1 ? MILEAGE_VEHICLE_COLUMN_ORDER.length : idx;
  };

  return [...vehicles].sort((a, b) => {
    const byColumn = orderIndex(a.description) - orderIndex(b.description);
    if (byColumn !== 0) return byColumn;
    return (a.displayOrder ?? 999) - (b.displayOrder ?? 999);
  });
}

function toFiniteNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
}

export function calculateMileageFare(
  distanceMiles: number,
  rates: MileageRateCell[],
  minimumFare?: number | null
): number {
  const minFare = toFiniteNumber(minimumFare) ?? 0;
  const tiers = rates
    .map((r) => ({
      uptoMiles: toFiniteNumber(r.toMiles) ?? 0,
      ratePerMile: toFiniteNumber(r.ratePerMile),
    }))
    .filter((r) => r.ratePerMile != null && r.ratePerMile > 0)
    .map((r) => ({ uptoMiles: r.uptoMiles, ratePerMile: r.ratePerMile! }))
    .sort((a, b) => a.uptoMiles - b.uptoMiles);
  if (!tiers.length) return minFare;

  let total = 0;
  if (distanceMiles <= tiers[0].uptoMiles) {
    total = distanceMiles * tiers[0].ratePerMile;
  } else {
    let remaining = distanceMiles;
    let prev = 0;
    for (const tier of tiers) {
      if (remaining <= 0) break;
      const tierDist =
        remaining <= tier.uptoMiles - prev ? remaining : tier.uptoMiles - prev;
      total += tierDist * tier.ratePerMile;
      remaining -= tierDist;
      prev = tier.uptoMiles;
    }
    if (remaining > 0) {
      total += remaining * tiers[tiers.length - 1].ratePerMile;
    }
  }
  if (minFare > 0 && total < minFare) {
    return minFare;
  }
  return Math.round(total * 100) / 100;
}

export function applyUplift(
  baseRate: number,
  mode: "percent" | "flat" | null,
  amount: number | null
): number {
  if (!mode || amount == null) return baseRate;
  if (mode === "percent") return Math.round(baseRate * (1 + amount / 100) * 100) / 100;
  return Math.round((baseRate + amount) * 100) / 100;
}

export function applyUpliftsFromBase(vehicles: MileageVehicle[]): MileageVehicle[] {
  const base = vehicles.find((v) => v.isMileageBase);
  if (!base) return vehicles;
  return vehicles.map((v) => {
    if (v.isMileageBase) return v;
    return {
      ...v,
      rates: v.rates.map((cell) => {
        const baseCell = base.rates.find((b) => b.serialNo === cell.serialNo);
        const baseRate = baseCell?.ratePerMile ?? 0;
        if (!baseRate) return { ...cell, ratePerMile: null };
        return {
          ...cell,
          ratePerMile: applyUplift(baseRate, v.upliftMode, v.upliftAmount),
        };
      }),
    };
  });
}

export function buildCalculatorRows(
  vehicles: MileageVehicle[],
  distances: number[]
) {
  return distances.map((distance) => ({
    distance,
    fares: vehicles.map((v) => ({
      vehicleTypeId: v.vehicleTypeId,
      description: v.description,
      fare: calculateMileageFare(distance, v.rates, v.minimumFare),
    })),
  }));
}

export function syncVehicleRatesToBands(
  vehicles: MileageVehicle[],
  bands: MileageBand[]
): MileageVehicle[] {
  return vehicles.map((vehicle) => ({
    ...vehicle,
    rates: bands.map((band) => {
      const existing = vehicle.rates.find((rate) => rate.serialNo === band.serialNo);
      return {
        serialNo: band.serialNo,
        fromMiles: band.fromMiles,
        toMiles: band.toMiles,
        ratePerMile: existing?.ratePerMile ?? null,
      };
    }),
  }));
}

export function createNextBand(bands: MileageBand[]): MileageBand {
  const last = bands[bands.length - 1];
  const fromMiles = last ? Math.round((last.toMiles + 0.01) * 100) / 100 : 0;
  const serialNo = bands.length ? Math.max(...bands.map((band) => band.serialNo)) + 1 : 1;
  return {
    serialNo,
    fromMiles,
    toMiles: fromMiles + 10,
  };
}
